import {Injectable} from '@angular/core';
import {AuditLogsService, LegalsService as Api} from '../../generated/sales-terminal-client/api/api';
import {
  AuditLogPage,
  BankAccount,
  ContactPerson,
  ContactPersonPost,
  InvoicingPeriodPut,
  LegalPerson,
  LegalPersonCore,
  LegalPersonList,
  LegalPersonPost,
  LegalPersonSimple,
  PaymentLink,
  Representative,
  RepresentativeBasic,
  RepresentativeInvite,
  RepresentativeInvitePost,
  SalespointRole,
  UserCore,
  ShopProvider,
  ShopProviderPost
} from '../../generated/sales-terminal-client';
import {ValidationService} from '../validation/validation.service';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import {PageableResult, ScrollLoadService, SortableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {ScrollLoad} from '../../components/scroll-load-component/ScrollLoad';

export interface LegalPersonGet extends SortableSearch {
  filters?: {
    companyName?: string;
    regNumber?: string;
    search?: string;
    name?: string;
    projectManagerId?: string;
    excludeUnclaimed?: boolean;
    includeAllCountries?: boolean;
  }
}

@Injectable()
export class LegalPersonService implements ScrollLoadService<LegalPersonList> {
  constructor(private _api: Api,
              private _auditLogsService: AuditLogsService,
              private snackService: SnackService) {}

  public search(data: LegalPersonGet): Observable<PageableResult<LegalPersonList>> {
    return this._api.legalPersonsGet(
      data.filters?.companyName,
      data.filters?.regNumber,
      data.filters?.search,
      data.filters?.name,
      data.filters?.projectManagerId,
      data.filters?.excludeUnclaimed,
      data.filters?.includeAllCountries,
      data.page,
      data.pageSize,
      data.sortBy,
      data.sortOrder
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public displayScrollLoadOption(option: LegalPersonList): string {
    return option.companyName;
  }
  public displayScrollLoadOptionNameAndRegCode(option: LegalPersonList): string {
    return option.companyName + (option.regNumber ? ' (' + option.regNumber.split('').join('&#8203;') + ')' : '') + '</b>';
  }
  public displayScrollLoadOptionNameAndCountryCode(option: LegalPersonList): string {
    return option.companyName + (option.countryCode ? ' [' + option.countryCode + ']' : '');
  }
  public displayScrollLoadSelection(selection: LegalPersonList): string | undefined {
    return selection?.companyName || undefined;
  }
  public displayScrollLoadSelectionNameAndRegCode(selection: LegalPersonList): string | undefined {
    if (selection?.companyName) {
      return selection.companyName + (selection.regNumber ? ' (' + selection.regNumber + ')' : '');
    } else {
      return '';
    }
  }
  public displayScrollLoadSelectionNameAndCountryCode(selection: LegalPersonList): string | undefined {
    if (selection?.companyName) {
      return selection.companyName +  (selection.countryCode ? ' [' + selection.countryCode + ']' : '');
    } else {
      return '';
    }
  }
  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<LegalPersonList> {
    return new ScrollLoad<LegalPersonList>(pageSize || 50, idKey || 'id');
  }

  public legalPersonsSalespoint(excludeWithoutCentre?: boolean): Observable<Array<LegalPersonSimple>> {
    return this._api.legalPersonsSalespoint(excludeWithoutCentre).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getLegalPersonRolesById(id: number): Observable<SalespointRole[]> {
    return this._api.getLegalPersonRolesById(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public get(id: number): Observable<LegalPerson> {
    return this._api.legalPersonGetById(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public add(data: LegalPersonPost): Observable<LegalPerson> {
    return this._api.legalPersonPost(data);

  }
  public update(id: number, data: LegalPersonPost): Observable<LegalPerson> {
    return this._api.legalPersonPutById(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public claimLegal(id: number): Observable<LegalPerson> {
    return this._api.legalpersonIdClaimPost(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getLegalPersonByOwnCentre(): Observable<LegalPersonCore> {
    return this._api.getLegalPersonByOwnCentre();
  }

  public validateLegalPerson(legalPerson: LegalPerson, contactAddressIsDifferent: boolean): boolean {
    if (!legalPerson) {
      return false;
    }
    const name: boolean = !!legalPerson.companyName && ValidationService.validateString(legalPerson.companyName, 3);
    const regNumber: boolean = !!legalPerson.regNumber && ValidationService.validateRegNumber(legalPerson.regNumber);
    const email: boolean = !!legalPerson.email || ValidationService.validateEmail(legalPerson.email);
    const phone: boolean = !legalPerson.phone || ValidationService.validatePhone(legalPerson.phone);
    const address: boolean = !!(legalPerson.address.countryCode && legalPerson.address.county && legalPerson.address.city && legalPerson.address.street && legalPerson.address.postalCode);
    const contactAddress: boolean = !contactAddressIsDifferent || !!(legalPerson.contactAddress.countryCode && legalPerson.contactAddress.county && legalPerson.contactAddress.city && legalPerson.contactAddress.street && legalPerson.contactAddress.postalCode);
    const salespoints: boolean = !legalPerson.salespoints?.length || !legalPerson.salespoints.filter(sp => {
      return !sp.id;
    }).length;
    const promoters: boolean = !legalPerson.promoters?.length || (!!legalPerson.promoters?.length && !!legalPerson.promoters.filter(p => (p > 0)).length &&
      !!legalPerson.salespoints.filter(sp => (sp.id)).length);
    const contactPersons: boolean = !legalPerson.contactPersons?.length || !legalPerson.contactPersons.filter(cp => {
      return !cp.id && (!cp.firstName || !ValidationService.validateString(cp.firstName, 2) || !cp.lastName
        || !ValidationService.validateString(cp.lastName, 2) || cp.phoneNr && !ValidationService.validatePhone(cp.phoneNr) || cp.email && !ValidationService.validateEmail(cp.email));
    }).length;
    return !!(name && regNumber && email && phone && address && contactAddress && salespoints && contactPersons && promoters);
  }

  public setPageableQuery(query: string): LegalPersonGet {
    return { filters: { search: query } };
  }

  public setPageableQueryExcludeUnclaimed(query: string): LegalPersonGet {
    return { filters: { search: query, excludeUnclaimed : true } };
  }

  public setPageableQueryCompanyName(query: string): LegalPersonGet {
    return { filters: { companyName: query } };
  }

  public setPageableVenueQuery(query: string): LegalPersonGet {
    return { filters: { search: query, includeAllCountries: true } };
  }

  public addContactPersons(id: number, data: ContactPersonPost[]): Observable<ContactPerson[]> {
    return this._api.legalpersonLegalIdContactPersonsPost(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public updateContactPerson(id: number, contactPerson: ContactPerson): Observable<ContactPerson> {
    return this._api.legalpersonLegalIdContactPersonContactIdPut(id, contactPerson.id, contactPerson).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeContactPerson(id: number, contactPersonId: number): Observable<any> {
    return this._api.legalpersonLegalIdContactPersonContactIdDelete(id, contactPersonId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addRepresentative(id: number, data: RepresentativeInvitePost): Observable<RepresentativeInvite> {
    return this._api.legalpersonLegalIdInvitePost(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public updateRepresentative(id: number, repUserId: string, roles: SalespointRole[]): Observable<Representative> {
    return this._api.legalpersonLegalIdRepresentativeUserIdPut(id, repUserId, roles).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeRepresentative(id: number, repUserId: string): Observable<any> {
    return this._api.legalpersonLegalIdRepresentativeUserIdDelete(id, repUserId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public resendInvite(id: number, inviteId: number): Observable<any> {
    return this._api.legalpersonLegalIdInviteInviteIdResendPost(id, inviteId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public updateInvite(id: number, inviteId: number, data: RepresentativeInvitePost): Observable<RepresentativeInvite> {
    return this._api.legalpersonLegalIdInviteInviteIdPut(id, inviteId, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public removeInvite(id: number, inviteId: number): Observable<any> {
    return this._api.legalpersonLegalIdInviteInviteIdDelete(id, inviteId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateInvoicingPeriod(id: number, data: InvoicingPeriodPut): Observable<LegalPerson> {
    return this._api.legalpersonIdInvoicingPut(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getBankAccounts(id: number): Observable<Array<BankAccount>> {
    return this._api.legalpersonIdBankAccountsGet(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getBankAccountPaymentLink(id: number): Observable<PaymentLink> {
    return this._api.legalpersonIdBankAccountPost(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addBankAccountManually(id: number, iban: string): Observable<BankAccount> {
    return this._api.legalpersonIdBankAccountIbanPut(id, iban).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public checkBankAccountStatus(paymentReference: string, eventName: string): Observable<any> {
    return this._api.publicLegalpersonBankAccountStatusGet(paymentReference, eventName).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getAuditLogs(id: number, page: number, pageSize: number): Observable<AuditLogPage> {
    return this._auditLogsService.getLegalAuditLogsByLegalId(id, page, pageSize).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public findLegalRepresentatives(externalId: number): Observable<RepresentativeBasic[]> {
    return this._api.findLegalRepresentativesBySalespoint(externalId).pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public findAllAssignedProjectManagersForLandCentre(): Observable<UserCore[]> {
    return this._api.findAllAssignedProjectManagersForLandCentre().pipe(
      tap({error: (e: any) => this.snackService.showBackendError(e)})
    );
  }

  public addShopProviders(legalPersonId: number, data: ShopProviderPost[]): Observable<ShopProvider[]> {
    return this._api.addShopProviders(legalPersonId, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public updateShopProvider(legalPersonId: number, shopProviderId: number, data: ShopProvider): Observable<ContactPerson> {
    return this._api.updateShopProvider(legalPersonId, shopProviderId, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public deleteShopProvider(legalPersonId: number, providerId: number): Observable<any> {
    return this._api.deleteShopProvider(legalPersonId, providerId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}

